import { Combobox, Field, ValidationMessages } from '@payaca/ambrosia';
import { usePrevious } from '@payaca/hooks/usePrevious';
import {
  LowCarbonTech,
  SearchDevice,
  SupportingLowCarbonTech,
} from '@payaca/types/deviceTypes';
import { parseHtmlToReact } from '@payaca/utilities/htmlUtilities';
import { sentenceCase } from '@payaca/utilities/stringUtilities';
import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, get, useFormContext, useWatch } from 'react-hook-form';
import useDeviceSearch from '../../../api/rest/connectionApplication/useDeviceSearch';
import { Props as DynamicField } from './DynamicField';
import SearchedDeviceDetailsCard from './SearchedDeviceDetailsCard';
import SearchedDeviceDetailsCardSkeleton from './SearchedDeviceDetailsCardSkeleton';

type Props = Omit<
  DynamicField,
  'definitions' | 'namespace' | 'dependentFields'
> & {
  fieldProps: any;
  deviceType: LowCarbonTech | SupportingLowCarbonTech;
};

const DeviceSearchField: FC<Props> = ({
  fieldProps,
  name,
  fieldSchema,
  deviceType,
}) => {
  const form = useFormContext();
  const selectedDeviceRef = useWatch({ name });
  const error = get(form.formState.errors, name);

  const [query, setQuery] = useState<string>(selectedDeviceRef);
  const [phaseCode] = form.getValues(['supplyDetails.phaseCode']);
  const previousDeviceType = usePrevious(deviceType);

  const {
    data: deviceSearchResults,
    refetch: fetchSearchDevice,
    isRefetching,
  } = useDeviceSearch(name, deviceType, query, phaseCode);

  const selectedDevice = useMemo(() => {
    const fullDevice = (deviceSearchResults || [])?.find(
      (x: SearchDevice) => x.reference === selectedDeviceRef
    );
    return fullDevice;
  }, [deviceSearchResults, selectedDeviceRef]);

  const options = useMemo(() => {
    return (
      (deviceSearchResults || [])?.map((x: SearchDevice) => ({
        value: x.reference,
        label: x.highlightedDisplayValue,
      })) || []
    ).slice(0, 20);
  }, [deviceSearchResults]);

  useEffect(() => {
    if (previousDeviceType && deviceType !== previousDeviceType) {
      // device type has been changed, reset the query
      setQuery('');
    }
  }, [deviceType, previousDeviceType]);

  useEffect(() => {
    // query changes - rerun the search
    void fetchSearchDevice();
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'fetchSearchDevice'
  }, [query]);

  return (
    <Field.Legacy {...fieldProps}>
      <Field.Label>
        {sentenceCase(fieldSchema.title)}{' '}
        {fieldSchema.json_schema_extra.required ? '*' : ''}
      </Field.Label>
      <Field.HelperText>
        {fieldSchema.json_schema_extra.tooltip}
      </Field.HelperText>
      <Controller
        control={form.control}
        name={name}
        render={({ field: { ref, value, ...rest } }) => (
          <Combobox
            {...rest}
            className="mb-3"
            value={value || null}
            options={options}
            query={query}
            setQuery={setQuery}
            customButtonContent={selectedDevice?.displayValue}
            placeholder="Start typing to search for a device..."
            filterFunction={null}
            CustomOption={({ option }) => (
              <span>{parseHtmlToReact(option.label)}</span>
            )}
          />
        )}
      />
      {error && (
        <ValidationMessages
          validationState={{
            isValid: false,
            validationMessages: ['This field is required'],
          }}
        />
      )}
      {selectedDevice ? (
        <SearchedDeviceDetailsCard selectedDevice={selectedDevice} />
      ) : isRefetching && query ? (
        <SearchedDeviceDetailsCardSkeleton className="mt-4" />
      ) : null}
    </Field.Legacy>
  );
};

export default DeviceSearchField;
